import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ticketApi } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { createOptimisticDelete } from '../utils/storeHelpers';

export interface Ticket {
  id: string;
  queueConfigId: string;
  ticketNumber: number;
  status: string;
  customerName: string | null;
  customerPhone: string | null;
  customerLineId: string | null;
  partySize: number;
  note: string | null;
  source: string;
  issuedAt: string;
  notifiedAt: string | null;
  calledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  estimatedWaitMinutes: number | null;
  accessToken: string;
  createdAt: string;
  updatedAt: string;
}

interface TicketState {
  waitingList: Ticket[];
  hasFetched: boolean;

  fetchWaitingList: () => Promise<void>;
  issueTicket: (data: any) => Promise<Ticket>;
  callNext: () => Promise<Ticket>;
  updateTicket: (id: string, updates: Partial<Ticket>) => void;
  updateStatus: (id: string, status: string) => Promise<void>;
  optimisticDelete: (id: string, deleteFn: (id: string) => Promise<any>) => Promise<void>;
  deleteTicket: (id: string) => Promise<void>;
}

export const useTicketStore = create<TicketState>()(
  persist(
    (set, get) => ({
      waitingList: [],
      hasFetched: false,

      fetchWaitingList: async () => {
        try {
          const response = await ticketApi.getWaiting();
          set({ waitingList: response.data, hasFetched: true });
        } catch (error: any) {
          console.error('Failed to fetch waiting list:', error);
        }
      },

      issueTicket: async (data) => {
        try {
          const response = await ticketApi.create(data);
          const newTicket = response.data;

          set((state) => ({
            waitingList: [...state.waitingList, newTicket],
          }));

          toast.success(i18n.t('queue.messages.ticketIssued', { number: newTicket.ticketNumber }));
          return newTicket;
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || '取號失敗';
          toast.error(errorMsg);
          throw error;
        }
      },

      callNext: async () => {
        try {
          const response = await ticketApi.callNext();
          const calledTicket = response.data;

          // 重新載入等候清單
          get().fetchWaitingList();

          toast.success(i18n.t('queue.messages.calledNumber', { number: calledTicket.ticketNumber }));
          return calledTicket;
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || '叫號失敗';
          toast.error(errorMsg);
          throw error;
        }
      },

      updateTicket: (id, updates) => {
        set((state) => ({
          waitingList: state.waitingList.map((t) =>
            t.id === id ? { ...t, ...updates } : t
          ),
        }));
      },

      updateStatus: async (id, status) => {
        // 1. 保存舊值
        const previousList = get().waitingList;

        // 2. 立即更新 UI（樂觀更新）
        get().updateTicket(id, { status });

        try {
          await ticketApi.updateStatus(id, status);
          // 重新載入確保資料一致性
          get().fetchWaitingList();
        } catch (error: any) {
          // 3. 失敗回滾
          set({ waitingList: previousList });
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
          toast.error(errorMsg);
        }
      },

      optimisticDelete: async (id, deleteFn) => {
        const fn = createOptimisticDelete<Ticket>('waitingList', '號碼牌');
        return fn(id, deleteFn, get, set);
      },

      deleteTicket: async (id) => {
        try {
          await get().optimisticDelete(id, ticketApi.delete);
          toast.success(i18n.t('common.messages.deleteSuccess'));
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.deleteFailed');
          toast.error(errorMsg);
        }
      },
    }),
    {
      name: 'ticket-storage',
      partialize: (state) => ({
        waitingList: state.waitingList,
        hasFetched: state.hasFetched,
      }),
    }
  )
);
